"use client"

import { createContext, useContext, type ReactNode } from "react"
import { useCountry } from "@/components/layout/country-provider"
import type { Country } from "@/i18n/config"
import type { GateId } from "@/lib/country-gates"
import {
  resolveSectionVisibility,
  type SectionVisibility,
} from "@/lib/section-visibility"

/**
 * CMS per-country section visibility, read once in
 * `[country]/[locale]/layout.tsx`. `null` = the global could not be read;
 * wherever no provider is mounted (isolated component tests) the default is
 * also `null`, so resolving a `section:` gate there throws unless the test
 * supplies a value.
 */
const SectionVisibilityContext = createContext<SectionVisibility | null>(null)

export function SectionVisibilityProvider({
  value,
  children,
}: {
  value: SectionVisibility | null
  children: ReactNode
}) {
  return (
    <SectionVisibilityContext.Provider value={value}>
      {children}
    </SectionVisibilityContext.Provider>
  )
}

/** The CMS section visibility in scope, or `null` when unreadable / no provider. */
export function useSectionVisibility(): SectionVisibility | null {
  return useContext(SectionVisibilityContext)
}

/** Client-side gate check: `section:` gates read the CMS value, others the code registry. */
export function useIsAllowedInCountry(id: GateId): boolean {
  const { code } = useCountry()
  return resolveSectionVisibility(id, code, useSectionVisibility())
}

/**
 * Same signature as `isAllowedInCountry`, but `section:` gates read the CMS value. For
 * components that gate several sections (or compute the answer inline).
 */
export function useSectionGate(): (id: GateId, country: Country) => boolean {
  const visibility = useSectionVisibility()
  return (id, country) => resolveSectionVisibility(id, country, visibility)
}
